import { produce } from 'immer';
import { isNil, values } from 'lodash/fp';
import { extractError } from 'app/lib/error';
import { splitFqn, toStringFqn } from 'app/lib/utils';
import {
    GET_DATAMEER_SNOWFLAKE_WAREHOUSE_SUCCESS_DATAMEER_DEFINED,
    GET_DATAMEER_SNOWFLAKE_WAREHOUSE_SUCCESS_NOT_YET_CONFIGURED,
    GET_DATAMEER_SNOWFLAKE_WAREHOUSE_SUCCESS_SNOWFLAKE_DEFAULT_DEFINED,
    GET_DATAMEER_SNOWFLAKE_WAREHOUSE_FAILURE_SNOWFLAKE_NOT_CONFIGURED
} from 'app/store/__generated__/events';
import { DELETE_PROJECT } from 'app/store/entities/types.js';
import { GET_SNOWFLAKE_CONFIGURATION_ACTION_TYPE } from 'app/store/snowflakeConfiguration/types';
import {
    GET_DATAMEER_SNOWFLAKE_WAREHOUSE,
    LIST_SNOWFLAKE_WAREHOUSES,
    LIST_USER_SESSION_WAREHOUSES,
    SET_DATAMEER_SNOWFLAKE_WAREHOUSE,
    GET_SNOWFLAKE_SOURCES,
    GET_SNOWFLAKE_SCHEMAS,
    GET_SNOWFLAKE_SOURCE_COLUMNS,
    LIST_SNOWFLAKE_ROLES,
    SET_SNOWFLAKE_ROLE,
    SET_USER_SESSION_WAREHOUSE,
    DELETE_SNOWFLAKE_TABLE,
    GET_SNOWFLAKE_DATABASES,
    SEARCH_SNOWFLAKE_OBJECTS,
    SEARCH_SNOWFLAKE_DESTINATIONS
} from './types.js';
import {
    SetSnowflakeWarehouseStatus,
    RESET_SET_DATAMEER_SNOWFLAKE_WAREHOUSE_ERRORS
} from './constants';
import {
    transformSnowflakeRolesResponse,
    transformSnowflakeResponse,
    getTypeFamily,
    transformSnowflakeSearchResults,
    transformSnowflakeSearchResultsDestination
} from './transformer.js';

export const INITIAL_STATE = {
    warehouses: {
        error: null,
        list: {
            isLoading: false,
            warehouses: null,
            default: null,
            isDefaultXS: false
        },
        get: {
            isLoading: false
        },
        set: {
            state: SetSnowflakeWarehouseStatus.IDLE
        },
        selectedWarehouse: { name: null, isConfigured: false }
    },
    userSessionWarehouses: {
        list: [],
        isLoading: false,
        itemInProgress: null
    },
    roles: {
        list: [],
        isLoading: false,
        itemInProgress: null,
        error: null
    },
    credentialError: null,
    locations: {
        databases: {
            byName: null,
            isLoading: false
        },
        schemas: {
            bySchemaFqn: {}
        },
        error: null,
        isFiltering: false,
        hasFilteredResults: false
    },
    destinationTreeLocations: {
        databases: {
            byName: null,
            isLoading: false
        },
        schemas: [],
        isFiltering: false,
        hasFilteredResults: false,
        error: null
    },
    columnsByFqn: {},
    isConnectionStatusModalOpen: false,
    isDeletingTable: false
};

export const snowflakeReducer = produce((draft, action) => {
    switch (action.type) {
        case SEARCH_SNOWFLAKE_DESTINATIONS.STARTED:
            draft.destinationTreeLocations.databases.byName = {};
            draft.destinationTreeLocations.schemas = [];
            draft.destinationTreeLocations.isFiltering = true;
            draft.destinationTreeLocations.error = null;
            break;
        case SEARCH_SNOWFLAKE_DESTINATIONS.ENDED:
            draft.destinationTreeLocations.isFiltering = false;
            break;
        case SEARCH_SNOWFLAKE_DESTINATIONS.SUCCEEDED: {
            const { databases, schemas } = transformSnowflakeSearchResultsDestination(
                action.payload.valuesByColumn[0][0]
            );
            draft.destinationTreeLocations.databases.byName = databases;
            draft.destinationTreeLocations.schemas = schemas;
            draft.destinationTreeLocations.hasFilteredResults = true;
            break;
        }
        case SEARCH_SNOWFLAKE_DESTINATIONS.FAILED:
            draft.destinationTreeLocations.error = extractError(action.payload);
            break;
        case SEARCH_SNOWFLAKE_OBJECTS.STARTED:
            draft.locations.databases.byName = {};
            draft.locations.error = null;
            draft.locations.schemas.bySchemaFqn = {};
            draft.locations.isFiltering = true;
            break;
        case SEARCH_SNOWFLAKE_OBJECTS.ENDED:
            draft.locations.isFiltering = false;
            break;
        case SEARCH_SNOWFLAKE_OBJECTS.SUCCEEDED: {
            const { databases, schemas } = transformSnowflakeSearchResults(
                action.payload.valuesByColumn[0][0]
            );
            draft.locations.databases.byName = databases;
            draft.locations.schemas.bySchemaFqn = schemas;
            draft.locations.hasFilteredResults = true;
            break;
        }
        case GET_SNOWFLAKE_DATABASES.STARTED: {
            const isDestinationTree = action.meta.isDestinationTree;
            if (!isDestinationTree) {
                draft.locations.databases.byName = {};
                draft.locations.error = null;
                draft.locations.databases.isLoading = true;
                draft.locations.schemas.bySchemaFqn = {};
                draft.locations.hasFilteredResults = false;
            }

            draft.destinationTreeLocations.databases.byName = {};
            draft.destinationTreeLocations.schemas = [];
            draft.destinationTreeLocations.databases.isLoading = true;
            draft.destinationTreeLocations.hasFilteredResults = false;
            draft.destinationTreeLocations.error = null;
            break;
        }
        case GET_SNOWFLAKE_SOURCES.STARTED: {
            const schemaFqn = action.meta.schemaFqn;
            if (draft.locations.schemas.bySchemaFqn[schemaFqn]) {
                draft.locations.schemas.bySchemaFqn[schemaFqn].isLoading = true;
            } else {
                const { database, schema } = splitFqn(schemaFqn);

                draft.locations.schemas.bySchemaFqn[schemaFqn] = {
                    database,
                    schema,
                    sources: {},
                    fqn: schemaFqn,
                    isLoading: true
                };
            }
            break;
        }
        case GET_SNOWFLAKE_SCHEMAS.STARTED: {
            const { database, isDestinationTree } = action.meta;
            const key = isDestinationTree ? 'destinationTreeLocations' : 'locations';
            draft[key].error = null;
            if (draft[key].databases.byName[database]) {
                draft[key].databases.byName[database].isLoading = true;
            }
            break;
        }
        case GET_SNOWFLAKE_SOURCES.ENDED: {
            const schemaFqn = action.meta.schemaFqn;
            if (draft.locations.schemas.bySchemaFqn[schemaFqn]) {
                draft.locations.schemas.bySchemaFqn[schemaFqn].isLoading = false;
            }
            break;
        }
        case GET_SNOWFLAKE_SCHEMAS.ENDED: {
            const { database, isDestinationTree } = action.meta;
            const key = isDestinationTree ? 'destinationTreeLocations' : 'locations';
            if (draft[key].databases.byName[database]) {
                draft[key].databases.byName[database].isLoading = false;
            }
            break;
        }
        case GET_SNOWFLAKE_DATABASES.ENDED: {
            draft.locations.databases.isLoading = false;
            draft.destinationTreeLocations.databases.isLoading = false;
            break;
        }
        case GET_SNOWFLAKE_DATABASES.SUCCEEDED: {
            const isDestinationTree = action.meta.isDestinationTree;
            const payload = transformSnowflakeResponse(action.payload);
            const databases = {};
            payload.forEach(({ name }) => {
                databases[name] = { name, isLoading: false };
            });
            if (!isDestinationTree) {
                draft.locations.databases.byName = databases;
            }
            draft.destinationTreeLocations.databases.byName = databases;
            break;
        }
        case GET_SNOWFLAKE_SOURCES.SUCCEEDED: {
            const schemaFqn = action.meta.schemaFqn;
            const payload = transformSnowflakeResponse(action.payload);
            const result = {};
            payload.forEach(({ database_name, schema_name, name, kind }) => {
                if (
                    schema_name !== 'INFORMATION_SCHEMA' &&
                    !name.startsWith('DATAMEER_HISTORY_') &&
                    kind !== 'TEMPORARY'
                ) {
                    const fqn = toStringFqn({
                        database: database_name,
                        schema: schema_name,
                        table: name
                    });
                    result[fqn] = {
                        database: database_name,
                        schema: schema_name,
                        table: name,
                        sourceType: kind,
                        fqn
                    };
                }
            });
            if (draft.locations.schemas.bySchemaFqn[schemaFqn]) {
                draft.locations.schemas.bySchemaFqn[schemaFqn].sources = result;
            }
            break;
        }
        case LIST_SNOWFLAKE_ROLES.STARTED: {
            draft.roles.isLoading = true;
            draft.userSessionWarehouses.list = [];
            break;
        }
        case SET_SNOWFLAKE_ROLE.STARTED:
            draft.roles.isLoading = true;
            draft.roles.itemInProgress = action.payload.role;
            break;
        case LIST_SNOWFLAKE_ROLES.SUCCEEDED: {
            const { currentRole, availableRoles } = transformSnowflakeRolesResponse(action.payload);

            draft.roles.list = availableRoles.map((name) => ({
                name,
                isSelected: name === currentRole
            }));
            draft.roles.itemInProgress = null;
            break;
        }
        case LIST_SNOWFLAKE_ROLES.FAILED:
            draft.roles.error = extractError(action.payload);
            break;
        case LIST_SNOWFLAKE_ROLES.ENDED:
            draft.roles.isLoading = false;
            break;
        case GET_SNOWFLAKE_SCHEMAS.SUCCEEDED: {
            const { isDestinationTree } = action.meta;
            const result = {};
            const payload = transformSnowflakeResponse(action.payload);

            payload.forEach(({ database_name, name }) => {
                if (name !== 'INFORMATION_SCHEMA') {
                    const fqn = toStringFqn({ database: database_name, schema: name });
                    result[fqn] = {
                        database: database_name,
                        schema: name,
                        sources: {},
                        fqn,
                        isLoading: false
                    };
                }
            });
            if (isDestinationTree) {
                draft.destinationTreeLocations.schemas.push(...values(result));
            } else {
                draft.locations.schemas.bySchemaFqn = {
                    ...draft.locations.schemas.bySchemaFqn,
                    ...result
                };
            }

            break;
        }
        case GET_SNOWFLAKE_DATABASES.FAILED: {
            const isDestinationTree = action.meta.isDestinationTree;
            if (!isDestinationTree) {
                draft.locations.error = extractError(action.payload);
            }
            draft.destinationTreeLocations.error = extractError(action.payload);
            break;
        }
        case GET_SNOWFLAKE_SOURCES.FAILED:
            draft.locations.error = extractError(action.payload);
            break;
        case GET_SNOWFLAKE_SOURCE_COLUMNS.STARTED: {
            const { fqn } = action.meta;

            draft.columnsByFqn[fqn] = { isLoading: true, columns: [] };
            break;
        }
        case GET_SNOWFLAKE_SOURCE_COLUMNS.SUCCEEDED: {
            const { fqn } = action.meta;
            const columns = transformSnowflakeResponse(action.payload);
            draft.columnsByFqn[fqn].columns = columns.map(({ column_name, data_type }) => ({
                name: column_name,
                typeFamily: getTypeFamily(data_type)
            }));
            break;
        }
        case GET_SNOWFLAKE_SOURCE_COLUMNS.ENDED: {
            const { fqn } = action.meta;

            draft.columnsByFqn[fqn].isLoading = false;
            break;
        }

        case LIST_SNOWFLAKE_WAREHOUSES.STARTED: {
            draft.warehouses.list.warehouses = null;
            draft.warehouses.list.isLoading = true;
            draft.warehouses.error = null;
            break;
        }

        case LIST_SNOWFLAKE_WAREHOUSES.SUCCEEDED:
            draft.warehouses.list.warehouses = action.payload.warehouses;
            draft.warehouses.list.default = action.payload.defaultWarehouse;
            break;
        case LIST_SNOWFLAKE_WAREHOUSES.FAILED: {
            draft.warehouses.list.warehouses = null;
            draft.warehouses.list.default = null;
            draft.warehouses.error = extractError(action.payload);
            break;
        }
        case LIST_SNOWFLAKE_WAREHOUSES.ENDED:
            draft.warehouses.list.isLoading = false;
            break;
        case LIST_USER_SESSION_WAREHOUSES.STARTED:
            draft.userSessionWarehouses.isLoading = true;
            break;
        case SET_USER_SESSION_WAREHOUSE.STARTED:
            draft.userSessionWarehouses.isLoading = true;
            draft.userSessionWarehouses.itemInProgress = action.payload.warehouse;
            break;
        case LIST_USER_SESSION_WAREHOUSES.SUCCEEDED: {
            const warehouses = transformSnowflakeResponse(action.payload);

            draft.userSessionWarehouses.list = warehouses.map(({ name, is_current }) => ({
                name,
                isSelected: is_current === 'Y'
            }));
            draft.userSessionWarehouses.itemInProgress = null;
            break;
        }
        case LIST_USER_SESSION_WAREHOUSES.ENDED:
            draft.userSessionWarehouses.isLoading = false;
            break;
        case GET_DATAMEER_SNOWFLAKE_WAREHOUSE.STARTED:
            draft.warehouses.get.isLoading = true;
            draft.warehouses.error = null;
            break;
        case GET_DATAMEER_SNOWFLAKE_WAREHOUSE.SUCCEEDED: {
            switch (action.payload._type) {
                case GET_DATAMEER_SNOWFLAKE_WAREHOUSE_SUCCESS_NOT_YET_CONFIGURED: {
                    draft.warehouses.selectedWarehouse.name = null;
                    draft.warehouses.selectedWarehouse.isConfigured = false;
                    draft.warehouses.set.state = SetSnowflakeWarehouseStatus.SUCCEEDED;
                    break;
                }
                case GET_DATAMEER_SNOWFLAKE_WAREHOUSE_SUCCESS_DATAMEER_DEFINED: {
                    draft.warehouses.selectedWarehouse.name = action.payload.name;
                    draft.warehouses.selectedWarehouse.isConfigured = true;
                    draft.warehouses.set.state = SetSnowflakeWarehouseStatus.SUCCEEDED;
                    break;
                }
                case GET_DATAMEER_SNOWFLAKE_WAREHOUSE_SUCCESS_SNOWFLAKE_DEFAULT_DEFINED: {
                    draft.warehouses.selectedWarehouse.name = null;
                    draft.warehouses.selectedWarehouse.isConfigured = true;
                    draft.warehouses.set.state = SetSnowflakeWarehouseStatus.SUCCEEDED;
                    break;
                }
                default:
            }
            break;
        }
        case GET_DATAMEER_SNOWFLAKE_WAREHOUSE.FAILED: {
            switch (action.payload.data?._type) {
                case GET_DATAMEER_SNOWFLAKE_WAREHOUSE_FAILURE_SNOWFLAKE_NOT_CONFIGURED: {
                    draft.warehouses.selectedWarehouse.isConfigured = false;
                    break;
                }
                default: {
                    draft.warehouses.selectedWarehouse.name = null;
                    draft.warehouses.error = extractError(action.payload);
                    break;
                }
            }
            break;
        }
        case GET_DATAMEER_SNOWFLAKE_WAREHOUSE.ENDED: {
            draft.warehouses.get.isLoading = false;
            break;
        }
        case SET_DATAMEER_SNOWFLAKE_WAREHOUSE.STARTED: {
            draft.warehouses.selectedWarehouse.name = action.payload.warehouse;
            draft.warehouses.set.state = SetSnowflakeWarehouseStatus.LOADING;
            break;
        }
        case SET_DATAMEER_SNOWFLAKE_WAREHOUSE.SUCCEEDED: {
            draft.warehouses.selectedWarehouse.isConfigured = true;
            draft.warehouses.selectedWarehouse.name = action.payload.name;
            draft.warehouses.set.state = SetSnowflakeWarehouseStatus.SUCCEEDED;
            draft.warehouses.list.isDefaultXS = action.payload.isSizeXSmall;
            break;
        }
        case SET_DATAMEER_SNOWFLAKE_WAREHOUSE.FAILED: {
            if (draft.warehouses.set.state === SetSnowflakeWarehouseStatus.LOADING) {
                draft.warehouses.set.state = SetSnowflakeWarehouseStatus.FAILED;
            }
            draft.warehouses.error = extractError(action.payload);
            break;
        }
        case RESET_SET_DATAMEER_SNOWFLAKE_WAREHOUSE_ERRORS: {
            draft.warehouses.set.state = SetSnowflakeWarehouseStatus.IDLE;
            break;
        }
        case DELETE_PROJECT.FAILED:
            draft.connection.getConfiguration.error = extractError(action.payload);
            break;

        case DELETE_SNOWFLAKE_TABLE.STARTED:
            draft.isDeletingTable = true;
            break;
        case DELETE_SNOWFLAKE_TABLE.PROGRESS: {
            const { database, schema, table } = action.params;
            const fqn = toStringFqn({ database, schema, table });
            const schemaFqn = toStringFqn({ database, schema });
            if (!isNil(draft.locations.schemas.bySchemaFqn[schemaFqn])) {
                delete draft.locations.schemas.bySchemaFqn[schemaFqn].sources[fqn];
            }

            delete draft.columnsByFqn[fqn];
            break;
        }
        case DELETE_SNOWFLAKE_TABLE.SUCCEEDED:
        case DELETE_SNOWFLAKE_TABLE.FAILED:
            draft.isDeletingTable = false;
            break;

        case GET_SNOWFLAKE_CONFIGURATION_ACTION_TYPE.STARTED: {
            draft.warehouses = INITIAL_STATE.warehouses;
            draft.userSessionWarehouses = INITIAL_STATE.userSessionWarehouses;
            draft.roles = INITIAL_STATE.roles;
            break;
        }

        default:
            break;
    }
}, INITIAL_STATE);
